import useResizeObserver from '@react-hook/resize-observer';
import {ComponentErrorBoundary} from 'components/ErrorBoundary';
import Spinner from 'components/Loaders/Spinner';
import {FC, RefObject, useEffect, useRef, useState} from 'react';
import {Document, Page, pdfjs} from 'react-pdf';
import {
    DocumentCallback,
    File,
    ScrollPageIntoViewArgs,
} from 'react-pdf/dist/cjs/shared/types';
import {twMerge} from 'tailwind-merge';
import {staticUrl} from 'utils/urls';

import ControlPanel from './ControlPanel';
import {pdfAutomaticWidth} from './ControlPanel/utils';
import {ScaleType} from './types';

// See node_modules/react-pdf/dist/cjs/Document.d.ts for types reference
type ViewerRef = {
    scrollPageIntoView: (args: ScrollPageIntoViewArgs) => void;
};

const useThrottledResizeObserver = (
    ref: RefObject<HTMLElement>,
    callback: (entry: ResizeObserverEntry) => void,
    delay = 100,
) => {
    const timeout = useRef<null | unknown>(null);
    useResizeObserver(ref, (entry) => {
        if (timeout) clearTimeout(timeout.current as number);
        timeout.current = setTimeout(() => callback(entry), delay);
    });
};

type PDFReaderProps = {
    className?: string;
    file: File;
    fileKey: string;
    initialPage?: number;
    initialScale?: ScaleType;
    showControls?: boolean;
};

const PDFReader: FC<PDFReaderProps> = ({
    className,
    file,
    fileKey,
    initialPage = 1,
    initialScale = 'automatic',
    showControls,
}) => {
    const viewerRef = useRef<null | ViewerRef>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [originalWidth, setOriginalWidth] = useState(0);
    const [scaleType, setScaleType] = useState<ScaleType>(initialScale);
    const [numberOfPages, setNumberOfPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [isLoading, setIsLoading] = useState(true);

    const [containerWidth, setContainerWidth] = useState(0);
    useThrottledResizeObserver(containerRef, (entry) => {
        setContainerWidth(entry.contentRect.width);
    });

    useEffect(() => {
        pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.mjs`;
    }, []);

    const onDocumentLoadSuccess = ({numPages}: DocumentCallback) => {
        setNumberOfPages(numPages);
        setIsLoading(false);
    };

    const calculateScale = () => {
        if (scaleType === 'automatic') {
            return pdfAutomaticWidth(originalWidth, containerWidth);
        }
        if (scaleType === null) return 1;

        return scaleType;
    };

    const pages = Array.from({length: numberOfPages}).map((_, index) => (
        <Page
            error={false}
            key={`page_${index + 1}`}
            loading={false}
            onLoadSuccess={({originalWidth: pageOriginalWidth}) => {
                if (pageOriginalWidth !== originalWidth) {
                    setOriginalWidth(pageOriginalWidth);
                }
            }}
            pageNumber={index + 1}
            renderAnnotationLayer={false}
            renderTextLayer={false}
            scale={calculateScale()}
        />
    ));

    // NOTE: Cmaps files exist in the Hats repo in /core/static/cmaps
    const options = {
        cMapUrl: staticUrl('cmaps/'),
    };

    return (
        <div
            className={twMerge(
                'flex flex-1 flex-col rounded-md p-2',
                'bg-grey-200 border border-grey-300',
                className,
            )}
        >
            {showControls && (
                <ControlPanel
                    numPages={numberOfPages}
                    pageNumber={currentPage}
                    scale={calculateScale()}
                    setPageNumber={(pageNumber) => {
                        if (
                            viewerRef.current &&
                            pageNumber > 0 &&
                            pageNumber <= numberOfPages
                        ) {
                            viewerRef.current.scrollPageIntoView({
                                pageNumber,
                            });
                        }
                        setCurrentPage(pageNumber);
                    }}
                    setScale={setScaleType}
                />
            )}
            <div className="relative flex-1">
                {isLoading && (
                    <div className="absolute left-0 top-0 z-10 flex size-full items-center justify-center bg-grey-200">
                        <Spinner className="m-auto" size="xl" />
                    </div>
                )}
                <div
                    className="absolute flex size-full overflow-scroll"
                    ref={containerRef}
                >
                    <ComponentErrorBoundary className="m-auto flex flex-col items-center">
                        <Document
                            className="mx-auto flex flex-col gap-2"
                            error={false}
                            file={file}
                            key={`${fileKey}${containerWidth}`}
                            loading={false}
                            onLoadSuccess={onDocumentLoadSuccess}
                            options={options}
                            ref={(ref) => {
                                if (ref?.viewer) {
                                    viewerRef.current = ref.viewer.current;
                                }
                            }}
                        >
                            {pages}
                        </Document>
                    </ComponentErrorBoundary>
                </div>
            </div>
        </div>
    );
};

export default PDFReader;
